'use client'

import React from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'

import { type Session } from '@rewardops/auth'
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icons,
  useLocale,
} from '@rewardops/ui'

import { LanguageSwitcher } from '~/app/i18n/language-switcher'
import { languageFromLocaleStr } from '~/app/i18n/settings'
import { useApi } from '../hooks/use-api'
import { useFormat } from '../hooks/use-format'
import { usePointsBalance } from '../hooks/use-points-balance'

interface MemberDropdownProps {
  session: Session
}

const MemberDropdown: React.FC<MemberDropdownProps> = ({
  session,
}: MemberDropdownProps) => {
  const { tenant } = useApi()
  const { language: currentLanguage } = useLocale()
  const { formatNumber } = useFormat()

  const languages = tenant?.locale?.supported
    ?.map(languageFromLocaleStr)
    ?.filter((lang) => lang !== currentLanguage)

  const isLangSwitcherShown = languages && languages?.length > 0

  // This Dropdown is hidden if the language switcher is hidden and the back to rewards center button is hidden
  const isDropdownShown =
    isLangSwitcherShown || tenant?.flags?.showBackToRewardsCenterButton

  const [isOpen, setIsOpen] = React.useState(false)

  const { t } = useTranslation()
  const points = usePointsBalance()

  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const program = session?.user?.program!
  const member = session.user!.member

  const getChevronIcon = () => {
    const className = 'ml-1 mt-2.5 h-5 w-5'
    return isOpen ? (
      <Icons.chevronUp className={className} role="image">
        <title>{t('general.closeDropdown')}</title>
      </Icons.chevronUp>
    ) : (
      <Icons.chevronDown className={className} role="image">
        <title>{t('general.openDropdown')}</title>
      </Icons.chevronDown>
    )
  }

  return (
    <DropdownMenu onOpenChange={(open) => setIsOpen(open)}>
      <DropdownMenuTrigger
        className="z-50 mt-0 flex cursor-pointer flex-row p-3 text-black hover:bg-slate-100"
        aria-expanded={undefined} // i18n fix: remove aria-expanded from child
        aria-label="Member Dropdown"
        role="button"
        tabIndex={0} // Allows the dropdown to be focused when tabbing
        asChild
      >
        <div>
          <div>
            <MemberButton member={member} />
            <div className=" text-slate-500">
              {formatNumber(points)} {t('general.points').toLowerCase()}
            </div>
          </div>
          {isDropdownShown && getChevronIcon()}
        </div>
      </DropdownMenuTrigger>
      {isDropdownShown && (
        <DropdownMenuContent className="rounded-xs bg-white">
          {tenant?.flags?.showBackToRewardsCenterButton && (
            <Link href={program.returnUrl || '#'}>
              <DropdownMenuItem className="cursor-pointer">
                {t('nav.backToRewardsCenter')}
              </DropdownMenuItem>
            </Link>
          )}
          {isLangSwitcherShown && (
            <LanguageSwitcher languages={languages} color="black" menuItem />
          )}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  )
}

function MemberButton({ member }: any) {
  const m = member as any
  return (
    <div className="py-1 font-semibold">
      <Avatar className="block h-6 w-10 md:hidden">
        <AvatarFallback>
          {m?.firstName?.charAt?.(0)}
          {m?.lastName?.charAt?.(0)}
        </AvatarFallback>
      </Avatar>
      <div className="hidden md:block">
        {member.firstName} {member.lastName}
      </div>
    </div>
  )
}

export default MemberDropdown
